import type { B2BAssets, HeadlineSection } from '../types';

export interface B2BHeadlineProps {
    section: HeadlineSection;
    assets: B2BAssets;
}

/**
 * Full-bleed photo-backed display headline. Used by two Figma sections:
 *
 *  - Market expansion (`443:24112`, 1920×1281): one Neue Haas Roman 80px (4.17vw)
 *    headline centred at ~45% down on a photo background.
 *  - Stats statement (`443:24330`, 1920×1281): same photo-bg pattern but with
 *    a 42.911px (2.235vw) subheadline below the main headline. Headline sits
 *    higher (`calc(50% − 8.22vw)` ≈ 37.7%); subheadline at `calc(50% + 3.0vw)`.
 *
 * Both layouts share `aspect-1920/1281`, a 10% black wash over the photo,
 * Neue Haas Roman 80px main type with `tracking-[-0.048em]` and `leading-[1.07]`.
 */
export const B2BHeadline = ({ section, assets }: B2BHeadlineProps) => {
    const backgroundSrc = section.backgroundAssetKey
        ? assets[section.backgroundAssetKey]
        : undefined;

    const headlineWidth = section.headlineWidthPercent ?? 93.5;
    const subheadlineWidth = section.subheadlineWidthPercent ?? 53.19;
    const subheadlineSize = section.subheadlineSizeVw ?? 2.235;
    const subheadlineTopOffset = section.subheadlineTopOffsetVw ?? 2.998;

    // Headline `top`: if an offset is provided, anchor to centre via vw maths;
    // otherwise fall back to the legacy `45%` used by the Market headline so its
    // pixel-pass result is preserved.
    const headlineTopStyle =
        section.headlineTopOffsetVw === undefined
            ? { top: '45%' }
            : {
                  top:
                      section.headlineTopOffsetVw < 0
                          ? `calc(50% - ${Math.abs(section.headlineTopOffsetVw)}vw)`
                          : `calc(50% + ${section.headlineTopOffsetVw}vw)`,
              };

    const subheadlineTopStyle = {
        top:
            subheadlineTopOffset < 0
                ? `calc(50% - ${Math.abs(subheadlineTopOffset)}vw)`
                : `calc(50% + ${subheadlineTopOffset}vw)`,
    };

    if (!backgroundSrc) {
        return (
            <section className="mx-auto flex w-full max-w-[120rem] flex-col items-center gap-6 px-6 py-[6vw] text-gray-900">
                <h2
                    className="whitespace-pre-line text-center font-sans text-[4.17vw] font-normal leading-[1.07] tracking-[-0.048em] max-md:text-3xl"
                    style={{ maxWidth: `${headlineWidth}%` }}
                >
                    {section.headline}
                </h2>
                {section.subheadline && (
                    <p
                        className="text-center font-sans font-normal leading-[1.07] tracking-[-0.048em] max-md:text-base"
                        style={{
                            maxWidth: `${subheadlineWidth}%`,
                            fontSize: `${subheadlineSize}vw`,
                        }}
                    >
                        {section.subheadline}
                    </p>
                )}
            </section>
        );
    }

    return (
        <section
            // Mobile (Figma `521:26187` 768×1178): aspect 0.6519 portrait.
            className="relative w-full overflow-hidden text-white max-md:aspect-768/1178!"
            style={{ aspectRatio: section.frameAspect ?? '1920/1281' }}
        >
            <img
                src={backgroundSrc}
                alt=""
                className="absolute inset-0 size-full object-cover object-bottom"
            />
            <div className="absolute inset-0 bg-black/10" />
            {/* `whitespace-pre-line` honours `\n` in the headline string so
                config can hard-break lines exactly where Figma does (the Market
                headline breaks after "Australia,").
                Mobile (Figma 521:26187): 40 px = 5.21 vw at 768, top 474/1178
                = 40.24 %, width 724/768 = 94.27 %. `!` overrides the desktop
                `top` set via inline style. */}
            <h2
                className="absolute left-1/2 -translate-x-1/2 whitespace-pre-line text-center font-sans text-[4.17vw] font-normal leading-[1.07] tracking-[-0.048em] max-md:top-[40.24%]! max-md:w-[94.27%]! max-md:text-[5.21vw]"
                style={{ ...headlineTopStyle, width: `${headlineWidth}%` }}
            >
                {section.headline}
            </h2>
            {section.subheadline && (
                <p
                    // Mobile (Figma `521:26858`): 25 px = 3.255 vw at 768,
                    // top calc(50% + 156.19px) → 63.26 % of 1178,
                    // width 666.47/768 = 86.78 %. `!` overrides desktop.
                    className="absolute left-1/2 -translate-x-1/2 text-center font-sans font-normal leading-[1.07] tracking-[-0.048em] max-md:top-[63.26%]! max-md:w-[86.78%]! max-md:text-[3.255vw]!"
                    style={{
                        ...subheadlineTopStyle,
                        width: `${subheadlineWidth}%`,
                        fontSize: `${subheadlineSize}vw`,
                    }}
                >
                    {section.subheadline}
                </p>
            )}
        </section>
    );
};
